// ComparisonSummary.js
// All Trades vs My Selected Trades comparison + Nifty 50 YTD benchmark.
// niftyData is fetched once in PortfolioPage and passed as a prop.
import React from 'react';

const DELTA_ROWS = [
    { label: 'Win Rate',          key: 'winRate',      unit: '%', higherBetter: true,  niftyApplicable: false },
    { label: 'Avg Positive',   key: 'avgRoiWin',    unit: '%', higherBetter: true,  niftyApplicable: true  },
    { label: 'Avg Return',     key: 'portfolioAvg', unit: '%', higherBetter: true,  niftyApplicable: true  },
    { label: 'Max Gain',          key: 'maxRoi',       unit: '%', higherBetter: true,  niftyApplicable: true  },
    { label: 'Avg Loss',          key: 'avgLoss',      unit: '%', higherBetter: false, niftyApplicable: false },
];

const fmtPct = (n, dec = 1) =>
    n == null ? '—' : (n >= 0 ? '+' : '') + n.toFixed(dec) + '%';

const fmtPrice = (n) =>
    n == null ? '—' : '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });

const badge = (diff, higherBetter) => {
    if (diff === 0) return { label: 'Same', cls: 'same' };
    const better = higherBetter ? diff > 0 : diff < 0;
    const sign   = diff > 0 ? '+' : '';
    return { label: `${sign}${diff}%`, cls: better ? 'better' : 'worse' };
};

const ComparisonSummary = ({ allStats, selectedStats, niftyData, onRefreshNifty }) => {
    const ytdPct   = niftyData?.ytdChangePercent ?? null;
    const ytdStart = niftyData?.ytdStartPrice    ?? null;
    const todayPct = niftyData?.todayChangePercent != null
        ? +niftyData.todayChangePercent.toFixed(2) : null;

    // Portfolio Alpha = My Avg Win ROI − Nifty YTD
    const alphaVal = (ytdPct != null && selectedStats.avgRoiWin != null)
        ? +(selectedStats.avgRoiWin - ytdPct).toFixed(1)
        : null;

    return (
        <>
            {/* ── Header: title + alpha badge ─────────────────────────────── */}
            <div className="cs-header">
                <div className="cs-header-title">Performance vs Nifty Benchmark</div>
                {alphaVal != null && (
                    <div className="cs-alpha-wrap">
                        <div className="cs-alpha-label">Portfolio Alpha (vs Nifty)</div>
                        <span className={`alpha-tag ${alphaVal >= 0 ? 'pos' : 'neg'}`}>
                            {alphaVal >= 0 ? '+' : ''}{alphaVal}%&nbsp;
                            {alphaVal >= 0 ? 'above' : 'below'} Nifty
                        </span>
                    </div>
                )}
            </div>

            {/* ── Two Nifty data cards + YTD return summary — hidden for now, re-enable when needed ──
            <div className="nifty-row">
                <div className="nifty-card">
                    <div className="nlc-label">
                        <span className="live-dot" />
                        Current Nifty 50
                        {niftyData?.cached && (
                            <span className="nlc-fetch-time">CACHED</span>
                        )}
                    </div>
                    {niftyData == null ? (
                        <div className="nifty-card-loading">
                            <span className="spinner-border spinner-border-sm" style={{ color: '#3871E0' }} />
                            &nbsp;Loading…
                        </div>
                    ) : (
                        <>
                            <div className="nlc-value">{fmtPrice(niftyData.currentPrice)}</div>
                            {todayPct != null && (
                                <div className={`nlc-chg ${todayPct >= 0 ? 'pos' : 'neg'}`}>
                                    {todayPct >= 0 ? '+' : ''}{todayPct}% today
                                </div>
                            )}
                            <div className="nlc-sub">Auto-fetched</div>
                        </>
                    )}
                    <button className="refresh-btn" onClick={onRefreshNifty}>↻ Refresh</button>
                </div>

                <div className="nifty-card">
                    <div className="nlc-label">Nifty YTD %</div>
                    {niftyData == null ? (
                        <div className="nifty-card-loading">
                            <span className="spinner-border spinner-border-sm" style={{ color: '#3871E0' }} />
                            &nbsp;Loading…
                        </div>
                    ) : (
                        <>
                            <div className={`nlc-value ${ytdPct != null && ytdPct >= 0 ? 'pos' : 'neg'}`}>
                                {fmtPct(ytdPct)}
                            </div>
                            <div className="nlc-sub">
                                Jan 1 2026 ({ytdStart
                                    ? ytdStart.toLocaleString('en-IN', { maximumFractionDigits: 2 })
                                    : '—'}) → Current
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="nr-single-row">
                <div className="nr-card">
                    <div className="nr-label">Nifty YTD Return (2026)</div>
                    <div className={`nr-val ${ytdPct != null && ytdPct >= 0 ? 'pos' : 'neg'}`}>
                        {fmtPct(ytdPct)}
                    </div>
                    <div className="nr-sub">Jan 1, 2026 → Current</div>
                </div>
            </div>
            ── */}

            {/* ── Three stat cards ─────────────────────────────────────────── */}
            <div className="comp3">
                <div className="comp-col">
                    <div className="comp-col-title green">All Recommendations</div>
                    {[
                        { key: 'Total',           val: allStats.total },
                        { key: 'Wins / Loss',     val: `${allStats.winners} / ${allStats.losers}` },
                        { key: 'Win Rate',        val: `${allStats.winRate}%`,          cls: allStats.winRate >= 60 ? 'g' : 'r' },
                        { key: 'Avg Positive', val: fmtPct(allStats.avgRoiWin),      cls: 'g' },
                        { key: 'Avg Return',   val: fmtPct(allStats.portfolioAvg),   cls: allStats.portfolioAvg >= 0 ? 'g' : 'r' },
                    ].map(({ key, val, cls }) => (
                        <div className="comp-row" key={key}>
                            <span className="comp-key">{key}</span>
                            <span className={`comp-val ${cls || ''}`}>{val}</span>
                        </div>
                    ))}
                </div>

                <div className="comp-col hl">
                    <div className="comp-col-title blue">My Recommendations</div>
                    {[
                        { key: 'Total',           val: selectedStats.total },
                        { key: 'Wins / Loss',     val: `${selectedStats.winners} / ${selectedStats.losers}` },
                        { key: 'Win Rate',        val: `${selectedStats.winRate}%`,              cls: selectedStats.winRate >= 60 ? 'g' : 'r' },
                        { key: 'Avg Positive', val: fmtPct(selectedStats.avgRoiWin),          cls: 'g' },
                        { key: 'Avg Return',   val: fmtPct(selectedStats.portfolioAvg),       cls: selectedStats.portfolioAvg >= 0 ? 'g' : 'r' },
                    ].map(({ key, val, cls }) => (
                        <div className="comp-row" key={key}>
                            <span className="comp-key">{key}</span>
                            <span className={`comp-val ${cls || ''}`}>{val}</span>
                        </div>
                    ))}
                </div>

                <div className="comp-col nf">
                    <div className="comp-col-title amber">Nifty 50 Benchmark</div>
                    {[
                        { key: 'Since Onboarding', val: '—' },
                        { key: 'YTD 2026',         val: fmtPct(ytdPct),                       cls: ytdPct != null && ytdPct >= 0 ? 'g' : 'r' },
                        { key: 'Onboarding Level', val: '—' },
                        { key: 'Current Level',    val: niftyData ? fmtPrice(niftyData.currentPrice) : '—' },
                        { key: 'Alpha',            val: alphaVal != null ? (alphaVal >= 0 ? '+' : '') + alphaVal + '%' : '—',
                          cls: alphaVal != null ? (alphaVal >= 0 ? 'g' : 'r') : '' },
                    ].map(({ key, val, cls }) => (
                        <div className="comp-row" key={key}>
                            <span className="comp-key">{key}</span>
                            <span className={`comp-val ${cls || ''}`}>{val}</span>
                        </div>
                    ))}
                </div>
            </div>

        </>
    );
};

export default ComparisonSummary;
